import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { addDictationMedCode } from "apiHandler/actions";
import ButtonProgress from "components/ButtonProgress";
import CommonCodeAutocomplete from "components/scribe/commonCodeAutoComplete";
import React, { useEffect, useState } from "react";

const MedCodeDialog = ({
  open,
  onClose,
  selectedCodeType,
  setSelectedCodeType,
  searchedCodes,
  inputValues,
  selectedCodes,
  loadingCode,
  onInputChange,
  setSelectedCodes,
  searchedModifiersList,
  modifierInputsList,
  modifierValuesList,
  loadingModifiersList,
  handleModifierInput,
  handleModifierValue,
  removeCodeField,
  isSaving,
  setInputValues,
  setModifierInputsList,
  setModifierValuesList,
  setSearchedModifiersList,
  setLoadingModifiersList,
  setModifierDebounceTimeoutsList,
  setIsSaving,
  details,
  userData,
  setSnackbarSeverity,
  getClaimDetails,
  setSnackbarMessage,
  setAddCodeDialogOpen,
  setSnackbarOpen,
}) => {
  const [showModifiers, setShowModifiers] = useState(
    selectedCodes.map(() => false)
  );

  const [noOfBoxes, setNoOfBoxes] = useState(9);

  useEffect(() => {
    const length = noOfBoxes;
    const padArray = (arr, defaultValue) =>
      arr.length > length
        ? arr.slice(0, length)
        : [...arr, ...Array(length - arr.length).fill(defaultValue)];

    setSelectedCodes((prev) => padArray(prev, null));
    setInputValues((prev) => padArray(prev, ""));
    setModifierInputsList((prev) => padArray(prev, ["", ""]));
    setModifierValuesList((prev) => padArray(prev, [null, null]));
    setSearchedModifiersList((prev) => padArray(prev, [[], []]));
    setLoadingModifiersList((prev) => padArray(prev, [false, false]));
    setModifierDebounceTimeoutsList((prev) => padArray(prev, [null, null]));
    setShowModifiers((prev) => padArray(prev, false));
  }, [noOfBoxes]);

  // Reset noOfBoxes and arrays on selectedCodeType change
  const handleCodeTypeChange = (e) => {
    const newType = e.target.value;
    setSelectedCodeType(newType);
    setNoOfBoxes(9); // Reset count to 9

    setSelectedCodes(Array(9).fill(null));
    setInputValues(Array(9).fill(""));
    setModifierInputsList(Array(9).fill(["", ""]));
    setModifierValuesList(Array(9).fill([null, null]));
    setSearchedModifiersList(Array(9).fill([[], []]));
    setLoadingModifiersList(Array(9).fill([false, false]));
    setModifierDebounceTimeoutsList(Array(9).fill([null, null]));
    setShowModifiers(Array(9).fill(false));
  };

  const toggleModifierVisibility = (idx) => {
    setShowModifiers((prev) => {
      const updated = [...prev];
      const toggledValue = !prev[idx];
      updated[idx] = toggledValue;

      // Always clear modifier values when toggling ON or OFF
      setModifierValuesList((prevMods) => {
        const newMods = [...prevMods];
        newMods[idx] = [null, null];
        return newMods;
      });

      setModifierInputsList((prevInputs) => {
        const newInputs = [...prevInputs];
        newInputs[idx] = ["", ""];
        return newInputs;
      });

      return updated;
    });
  };

  const onRemoveCodeField = (index) => {
    setNoOfBoxes((prev) => prev - 1);
    removeCodeField(index);
  };

  const handleCancel = () => {
    onClose();
    setNoOfBoxes(9);
    setShowModifiers(selectedCodes.map(() => false));
  };

  const onAddCodes = async () => {
    if (selectedCodes.length === 0) return;

    setIsSaving(true);

    // Build an array of record_data objects for all selected codes
    const recordDataArray = selectedCodes
      .filter(Boolean) // remove null or undefined codes
      .map((code, idx) => ({
        practice_id: details.practice_id,
        npi: details.npi,
        dictation_id: details.id,
        code_type: selectedCodeType,
        code: code.code,
        user_name: `${userData.first_name} ${userData.last_name}`,
        modifier1: modifierValuesList[idx]?.[0]?.code || "",
        modifier2: modifierValuesList[idx]?.[1]?.code || "",
      }));

    const payload = {
      type: "Dictation",
      record_data: recordDataArray, // send as array
    };

    try {
      // Call the API once with the full payload batch
      const res = await addDictationMedCode(payload, userData?.access_token);

      if (!res || res.Status !== "Success") {
        throw new Error("Failed to add medical codes");
      }

      setSnackbarSeverity("success");
      setSnackbarMessage("Codes added successfully");
      setAddCodeDialogOpen(false);

      // Reset state
      setSelectedCodes([null]);
      setInputValues([""]);
      setModifierInputsList([["", ""]]);
      setModifierValuesList([[null, null]]);
      setSearchedModifiersList([[[], []]]);
      setLoadingModifiersList([[false, false]]);
      setModifierDebounceTimeoutsList([[null, null]]);
      setNoOfBoxes(9);
      setShowModifiers(selectedCodes.map(() => false));

      getClaimDetails();
    } catch (error) {
      console.error(error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Something went wrong. Please try again!");
    } finally {
      setIsSaving(false);
      setSnackbarOpen(true);
    }
  };

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={handleCancel}>
      <DialogTitle>Add Medical Codes</DialogTitle>
      <DialogContent>
        <Select
          value={selectedCodeType}
          onChange={handleCodeTypeChange}
          fullWidth
          sx={{ mb: 2 }}
        >
          <MenuItem value="CPT">CPT</MenuItem>
          <MenuItem value="ICD10">ICD10</MenuItem>
        </Select>
        <Box display={"flex"} mb={2} alignItems={"center"}>
          <TextField
            value={noOfBoxes}
            type="number"
            onChange={(e) => setNoOfBoxes(e.target.value)}
            size="small"
            label="Number of codes"
          />
        </Box>
        {selectedCodes.map((code, idx) => (
          <Box key={idx} mb={3} p={2} border={1} borderRadius={1}>
            <CommonCodeAutocomplete
              label={`Search ${
                selectedCodeType === "CPT" ? "Procedure" : "Diagnosis"
              } Code`}
              options={searchedCodes}
              inputValue={inputValues[idx] || ""}
              value={code}
              onValueChange={(val) => {
                const newSelected = [...selectedCodes];
                newSelected[idx] = val;
                setSelectedCodes(newSelected);
              }}
              onInputChange={(e, val) => onInputChange(idx, val)}
              loading={loadingCode}
              onSelect={(val) => {
                const newSelected = [...selectedCodes];
                newSelected[idx] = val;
                setSelectedCodes(newSelected);
              }}
            />
            {selectedCodeType === "CPT" && (
              <Box mb={2} display={"flex"} alignItems={"center"}>
                <Switch
                  checked={showModifiers[idx]}
                  onChange={() => toggleModifierVisibility(idx)}
                  title="Add Modifier"
                />
                <Typography>Add Modifier</Typography>
              </Box>
            )}
            {showModifiers[idx] && selectedCodeType === "CPT" && (
              <Box mt={2}>
                {[0, 1].map((modIdx) => (
                  <CommonCodeAutocomplete
                    key={modIdx}
                    label={`Modifier ${modIdx + 1}`}
                    options={searchedModifiersList[idx]?.[modIdx] || []}
                    inputValue={modifierInputsList[idx]?.[modIdx] || ""}
                    value={modifierValuesList[idx]?.[modIdx] || null}
                    onValueChange={(val) =>
                      handleModifierValue(idx, modIdx, val)
                    }
                    onInputChange={handleModifierInput(idx, modIdx)}
                    loading={loadingModifiersList[idx]?.[modIdx] || false}
                    onSelect={(val) => handleModifierValue(idx, modIdx, val)}
                  />
                ))}
              </Box>
            )}

            <Button
              onClick={() => onRemoveCodeField(idx)}
              color="error"
              size="small"
              sx={{ mt: 1 }}
            >
              Remove
            </Button>
          </Box>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={onAddCodes}
          disabled={selectedCodes.length === 0 || isSaving}
          variant="contained"
        >
          {isSaving ? <ButtonProgress size={14} /> : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedCodeDialog;
